import React, { useMemo, useState } from 'react';
import { PencilLine, Eye, EyeOff, X, Trash2, RotateCcw } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';

import {
  parseBody,
  serializeFoodStatus,
  spliceLine,
  dividerLabel,
  isTreeFormat,
  type LogicalLine,
  type RackOption,
} from './lineModel';
import { FoodStatusForm } from './FoodStatusForm';
import { TypedLineForm } from './TypedLineForm';
import { KeypointBlockForm } from './KeypointBlockForm';
import { CategoryTreeForm } from './CategoryTreeForm';
import { InlineLongQueueRow, InlineLongestQueueRow, InlineNoVisitorsRow } from './InlineQueueRows';
import { ChecklistForm } from './ChecklistForm';
import { ReadOnlyBody } from '../ReadOnlyBody';
import { Tooltip } from '@/components/ui/tooltip';

// Render one row's inline html, opening real links in a new tab (as ReportBody does).
function rowHtml(row: string): string {
  return row
    .replace(/^<blockquote>/, '')
    .replace(/<\/blockquote>$/, '')
    .replace(/<a\s+/gi, '<a target="_blank" rel="noreferrer" ');
}

/**
 * V2 constructor: renders the report as sections (split by ━ dividers). Each section
 * header carries an include/exclude toggle (D3). Lines of a *modelled* type carry an
 * edit control that opens a proper template form INLINE (left column) so the chart on
 * the right stays visible — the form takes semantic variables and regenerates the
 * exact line (conditions + wording) rather than exposing raw text fields. Lines we
 * can't model are shown as plain text and edited via the Форматирование/Исходный tabs.
 */
export function ConstructorView({
  value,
  onChange,
  day,
  racksByType,
  openTime,
  maxCashiers,
  hiddenSections,
  onToggleSection,
  disabled,
  onOpenRack,
  hiddenRows,
  onToggleRow,
}: {
  value: string;
  onChange: (bodyHtml: string) => void;
  day: string;
  racksByType: Record<string, RackOption[]>;
  openTime: string | null;
  maxCashiers: number | null;
  hiddenSections: Set<string>;
  onToggleSection: (key: string) => void;
  disabled?: boolean;
  /** A category-header link (`?target=racks&rack=<name>`, build_problem_zone_link)
   *  selects that rack in the consult panel on the right instead of opening a
   *  new tab (Alexey 2026-09-07). Absent → links keep their new-tab default. */
  onOpenRack?: (
    rackName: string | undefined,
    window: [number, number] | undefined,
    rackId?: number,
  ) => void;
  /** Rows/blocks removed by the validator (Alexey 2026-09-04 spec: a per-row
   *  "убрать" next to the pencil; removed rows stay on screen greyed with an
   *  undo, and are stripped on save — see stripHiddenRows). Keyed by the
   *  block's rows joined by '\n'. */
  hiddenRows?: Set<string>;
  onToggleRow?: (key: string) => void;
}) {
  const { t } = useT();
  const treeFormat = useMemo(() => isTreeFormat(value), [value]);
  const removedRows = hiddenRows ?? new Set<string>();
  const rowKeyOf = (line: LogicalLine) => rows.slice(line.startIdx, line.endIdx + 1).join('\n');
  const removeProps = (line: LogicalLine) => {
    const k = rowKeyOf(line);
    return {
      removed: removedRows.has(k),
      onRemove: onToggleRow ? () => onToggleRow(k) : undefined,
      onRestore: onToggleRow ? () => onToggleRow(k) : undefined,
    };
  };
  // Rows are raw html (dangerouslySetInnerHTML), so intercept at the container.
  const onBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onOpenRack) return;
    const a = (e.target as HTMLElement).closest?.('a[href]') as HTMLAnchorElement | null;
    if (!a) return;
    const href = a.getAttribute('href') ?? '';
    const qi = href.indexOf('?');
    if (qi < 0) return;
    const params = new URLSearchParams(href.slice(qi + 1).replace(/&amp;/g, '&'));
    if (params.get('target') !== 'racks') return;
    e.preventDefault();
    const from = Number(params.get('from'));
    const to = Number(params.get('to'));
    const rackId = Number(params.get('rack_id'));
    onOpenRack(
      params.get('rack') ?? undefined,
      Number.isFinite(from) && Number.isFinite(to) && to > from ? [from, to] : undefined,
      Number.isInteger(rackId) && rackId > 0 ? rackId : undefined,
    );
  };
  const lines = useMemo(() => parseBody(value, openTime), [value, openTime]);
  const rows = useMemo(() => value.split('\n'), [value]);
  // Which line is being edited (matched by its start row + kind within this parse).
  const [editKey, setEditKey] = useState<string | null>(null);

  const rackTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(lines.flatMap((l) => (l.kind === 'food_status' ? [l.fields.rackType] : []))),
      ),
    [lines],
  );

  const applyRows = (line: LogicalLine, newRows: string[]) => {
    onChange(spliceLine(value, line, newRows));
    setEditKey(null);
  };

  // Alexey (2026-09-03, direct instruction): legacy (pre-rewrite) report
  // formats become view-only — no per-line forms here, just the formatted
  // text (still with the ━ section hide/show toggle, since that doesn't
  // depend on any per-line classification).
  if (!treeFormat) {
    return (
      <ReadOnlyBody
        value={value}
        hiddenSections={hiddenSections}
        onToggleSection={onToggleSection}
        disabled={disabled}
      />
    );
  }

  let curKey = '';
  let curHidden = false;

  return (
    <div
      className="report-body flex flex-col rounded-md border border-border bg-background p-3 text-sm leading-relaxed"
      onClick={onBodyClick}
    >
      {lines.map((line, li) => {
        if (line.kind === 'other' && line.isDivider) {
          curKey = dividerLabel(line.text);
          curHidden = hiddenSections.has(curKey);
          const key = curKey;
          return (
            <div key={li} className="mb-1 mt-3 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span
                className={cn(
                  'text-[11px] font-semibold uppercase tracking-wider',
                  curHidden ? 'text-muted-foreground/50 line-through' : 'text-muted-foreground',
                )}
              >
                {key}
              </span>
              <Tooltip content={t(curHidden ? 'reportValidation.constructor.unhide' : 'reportValidation.constructor.hide')}><button
                type="button"
                onClick={() => onToggleSection(key)}
                disabled={disabled}
                aria-label={t(curHidden ? 'reportValidation.constructor.unhide' : 'reportValidation.constructor.hide')}
                className={cn(
                  'shrink-0 rounded p-0.5 text-muted-foreground transition hover:bg-muted hover:text-foreground',
                  disabled && 'pointer-events-none opacity-30',
                )}
              >
                {curHidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              </button></Tooltip>
              <span className="h-px flex-1 bg-border" />
            </div>
          );
        }

        // Excluded section — its lines stay visible, greyed (the divider's
        // eye toggle is the undo), instead of vanishing (Alexey 2026-09-04).
        if (curHidden) {
          if (line.kind === 'other' && line.isBlank) return <div key={li} className="h-2" />;
          return (
            <div key={li} className="py-0.5 opacity-40 line-through">
              {rows.slice(line.startIdx, line.endIdx + 1).map((row, i) => (
                <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
              ))}
            </div>
          );
        }

        // ── Modelled type: food_status → inline template form ──
        if (line.kind === 'food_status') {
          const key = `food:${line.startIdx}`;
          const isEditing = editKey === key;
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          return (
            <div key={li} className="flex flex-col">
              <LineRow
                editing={isEditing}
                disabled={disabled}
                {...removeProps(line)}
                onEdit={() => setEditKey(isEditing ? null : key)}
                editLabel={t('reportValidation.constructor.editStatusLine')}
              >
                <div className="min-w-0 flex-1">
                  {span.map((row, i) =>
                    row.startsWith('<blockquote') ? (
                      <blockquote key={i} className="mt-0.5" dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                    ) : (
                      <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                    ),
                  )}
                </div>
              </LineRow>
              {isEditing && !removedRows.has(rowKeyOf(line)) && (
                <InlinePanel onClose={() => setEditKey(null)}>
                  <FoodStatusForm
                    initial={line.fields}
                    rackTypeOptions={rackTypeOptions}
                    racksByType={racksByType}
                    day={day}
                    onApply={(fields) => applyRows(line, serializeFoodStatus(fields))}
                    onCancel={() => setEditKey(null)}
                  />
                </InlinePanel>
              )}
            </div>
          );
        }

        // ── Keypoint category block → inline block form ──
        if (line.kind === 'keypoint_block') {
          const key = `kp:${line.startIdx}`;
          const isEditing = editKey === key;
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          return (
            <div key={li} className="flex flex-col">
              <LineRow
                editing={isEditing}
                disabled={disabled}
                {...removeProps(line)}
                onEdit={() => setEditKey(isEditing ? null : key)}
                editLabel={t('reportValidation.constructor.editLine')}
              >
                <div className="min-w-0 flex-1">
                  {span.map((row, i) => (
                    <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                  ))}
                </div>
              </LineRow>
              {isEditing && !removedRows.has(rowKeyOf(line)) && (
                <InlinePanel onClose={() => setEditKey(null)}>
                  <KeypointBlockForm
                    initial={line.block}
                    onApply={(newRows) => applyRows(line, newRows)}
                    onCancel={() => setEditKey(null)}
                  />
                </InlinePanel>
              )}
            </div>
          );
        }

        // ── Typed queue line → inline template form ──
        if (line.kind === 'typed') {
          const key = `typed:${line.startIdx}`;
          const isEditing = editKey === key;
          return (
            <div key={li} className="flex flex-col">
              <LineRow
                editing={isEditing}
                disabled={disabled}
                {...removeProps(line)}
                onEdit={() => setEditKey(isEditing ? null : key)}
                editLabel={t('reportValidation.constructor.editLine')}
              >
                <div
                  className="min-w-0 flex-1"
                  dangerouslySetInnerHTML={{ __html: rowHtml(rows[line.startIdx] ?? '') }}
                />
              </LineRow>
              {isEditing && !removedRows.has(rowKeyOf(line)) && (
                <InlinePanel onClose={() => setEditKey(null)}>
                  <TypedLineForm
                    line={line}
                    day={day}
                    maxCashiers={maxCashiers}
                    onApply={(row) => applyRows(line, [row])}
                    onCancel={() => setEditKey(null)}
                  />
                </InlinePanel>
              )}
            </div>
          );
        }

        // ── Tree-format "Выкладка" category block → inline template form ──
        if (line.kind === 'category_tree') {
          const key = `cat:${line.startIdx}`;
          const isEditing = editKey === key;
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          return (
            <div key={li} className="flex flex-col">
              <LineRow
                editing={isEditing}
                disabled={disabled}
                {...removeProps(line)}
                onEdit={() => setEditKey(isEditing ? null : key)}
                editLabel={t('reportValidation.tree.editCategory')}
              >
                <div className="min-w-0 flex-1">
                  {span.map((row, i) => (
                    <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                  ))}
                </div>
              </LineRow>
              {isEditing && !removedRows.has(rowKeyOf(line)) && (
                <InlinePanel onClose={() => setEditKey(null)}>
                  <CategoryTreeForm
                    initial={line.fields}
                    openTime={openTime}
                    onApply={(newRows) => applyRows(line, newRows)}
                    onCancel={() => setEditKey(null)}
                  />
                </InlinePanel>
              )}
            </div>
          );
        }

        // ── "Самая длинная очередь" — edited IN PLACE (Alexey 2026-09-04 spec) ──
        if (line.kind === 'longest_queue') {
          return (
            <LineRow key={li} disabled={disabled} {...removeProps(line)}>
              <div className="min-w-0 flex-1">
                <InlineLongestQueueRow
                  key={rowKeyOf(line)}
                  initial={line.fields}
                  disabled={disabled}
                  onChange={(newRows) => onChange(spliceLine(value, line, newRows))}
                />
              </div>
            </LineRow>
          );
        }

        // ── "Очередь от N человек" — edited IN PLACE ──
        if (line.kind === 'long_queue') {
          return (
            <LineRow key={li} disabled={disabled} {...removeProps(line)}>
              <div className="min-w-0 flex-1">
                <InlineLongQueueRow
                  key={rowKeyOf(line)}
                  initial={line.fields}
                  maxCashiers={maxCashiers}
                  disabled={disabled}
                  onChange={(row) => onChange(spliceLine(value, line, row.split('\n')))}
                />
              </div>
            </LineRow>
          );
        }

        // ── "Без посетителей у кассы" — edited IN PLACE ──
        if (line.kind === 'no_visitors') {
          return (
            <LineRow key={li} disabled={disabled} {...removeProps(line)}>
              <div className="min-w-0 flex-1">
                <InlineNoVisitorsRow
                  key={rowKeyOf(line)}
                  initial={line.fields}
                  disabled={disabled}
                  onChange={(row) => onChange(spliceLine(value, line, [row]))}
                />
              </div>
            </LineRow>
          );
        }

        // ── Tree-format "Чеклист" section → inline template form ──
        if (line.kind === 'checklist') {
          const key = `cl:${line.startIdx}`;
          const isEditing = editKey === key;
          const span = rows.slice(line.startIdx, line.endIdx + 1);
          return (
            <div key={li} className="flex flex-col">
              <LineRow
                editing={isEditing}
                disabled={disabled}
                {...removeProps(line)}
                onEdit={() => setEditKey(isEditing ? null : key)}
                editLabel={t('reportValidation.tree.editChecklist')}
              >
                <div className="min-w-0 flex-1">
                  {span.map((row, i) => (
                    <div key={i} dangerouslySetInnerHTML={{ __html: rowHtml(row) }} />
                  ))}
                </div>
              </LineRow>
              {isEditing && !removedRows.has(rowKeyOf(line)) && (
                <InlinePanel onClose={() => setEditKey(null)}>
                  <ChecklistForm
                    initial={line.fields}
                    onApply={(newRows) => applyRows(line, newRows)}
                    onCancel={() => setEditKey(null)}
                  />
                </InlinePanel>
              )}
            </div>
          );
        }

        // ── Unmodelled lines: plain text (no per-line form, but removable) ──
        if (line.kind === 'other' && line.isBlank) return <div key={li} className="h-2" />;
        return (
          <LineRow key={li} disabled={disabled} {...removeProps(line)}>
            <div className="min-w-0 flex-1" dangerouslySetInnerHTML={{ __html: rowHtml(line.text) }} />
          </LineRow>
        );
      })}
    </div>
  );
}

function LineRow({
  children,
  onEdit,
  editLabel,
  editing,
  disabled,
  removed,
  onRemove,
  onRestore,
}: {
  children: React.ReactNode;
  onEdit?: () => void;
  editLabel?: string;
  editing?: boolean;
  disabled?: boolean;
  /** Removed by the validator: shown greyed, pencil replaced by an undo. */
  removed?: boolean;
  onRemove?: () => void;
  onRestore?: () => void;
}) {
  const { t } = useT();
  const iconBtn = (active: boolean) =>
    cn(
      'mt-0.5 shrink-0 rounded-full p-0.5 transition',
      active
        ? 'bg-primary/10 text-primary opacity-100'
        : 'opacity-50 hover:bg-muted hover:opacity-100 focus-visible:opacity-100',
      disabled && 'pointer-events-none opacity-20',
    );
  return (
    <div className={cn('group flex items-start gap-2 py-0.5', removed && 'opacity-40 line-through')}>
      {children}
      {removed ? (
        onRestore && (
          <Tooltip content={t('reportValidation.constructor.restoreLine')}><button
            type="button"
            onClick={onRestore}
            disabled={disabled}
            aria-label={t('reportValidation.constructor.restoreLine')}
            className={cn(iconBtn(false), 'text-primary')}
          >
            <RotateCcw className="size-4" />
          </button></Tooltip>
        )
      ) : (
        <>
          {onEdit && (
            <Tooltip content={editLabel}><button
              type="button"
              onClick={onEdit}
              disabled={disabled}
              aria-label={editLabel}
              className={cn(iconBtn(Boolean(editing)), 'text-primary')}
            >
              <PencilLine className="size-4" />
            </button></Tooltip>
          )}
          {onRemove && (
            <Tooltip content={t('reportValidation.constructor.removeLine')}><button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              aria-label={t('reportValidation.constructor.removeLine')}
              className={cn(iconBtn(false), 'text-muted-foreground hover:text-destructive')}
            >
              <Trash2 className="size-4" />
            </button></Tooltip>
          )}
        </>
      )}
    </div>
  );
}

function InlinePanel({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="relative my-2 rounded-md border border-primary/30 bg-muted/30 p-3">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label="close"
      >
        <X className="size-3.5" />
      </button>
      {children}
    </div>
  );
}
